import { PmoReportScopeFilter } from './pmoSpecConfig';

/** KPIs que usam worklogs no intervalo De/Até (metodologia por período — distinta do HH acumulado Jira). */
export const PERIOD_WORKLOG_KPI_IDS = [
  'hh_real_worklog_period',
  'hh_vendido_relatorio',
  'consumo_hh_relatorio_percent',
] as const;

export type PeriodWorklogKpiId = typeof PERIOD_WORKLOG_KPI_IDS[number];

export const PERIOD_WORKLOG_KPI_OPTIONS: Array<{ id: PeriodWorklogKpiId; label: string }> = [
  { id: 'hh_real_worklog_period', label: 'HH Real (período)' },
  { id: 'hh_vendido_relatorio', label: 'HH Vendido (com apontamento no período)' },
  { id: 'consumo_hh_relatorio_percent', label: '% Consumo (período)' },
];

export const isPeriodWorklogKpiId = (id: string): id is PeriodWorklogKpiId =>
  (PERIOD_WORKLOG_KPI_IDS as readonly string[]).includes(id);

export type PeriodReportScopeApi = 'planilha_sap_nsap' | 'planilha_export_worklogs' | null;

export type PeriodWorklogScopeOption = {
  id: PmoReportScopeFilter;
  label: string;
  hint: string;
};

/** Opções de escopo para KPIs de HH no período (De/Até). Não altera KPIs clássicos. */
export const PERIOD_WORKLOG_SCOPE_OPTIONS: PeriodWorklogScopeOption[] = [
  {
    id: 'carteira',
    label: 'Histórias filtradas (todas subtarefas)',
    hint: 'Soma worklogs no De/Até de todas as histórias do filtro PMO, incluindo subtarefas.',
  },
  {
    id: 'planilha_sap_nsap',
    label: 'Marcadores SAP+NSAP (escopo relatório)',
    hint: 'Conta só issues/subtarefas cujo resumo bate marcadores do relatório Compras-e.',
  },
  {
    id: 'planilha_export_worklogs',
    label: 'Relatório Compras-e SAP+NSAP (Jira)',
    hint: 'Como o Excel: duas abas de apontamentos. SAP = Σ Horas trabalhadas nos filhos de ES47; NSAP = CMPE + filhos de ES25/ES43/ES01/ES97. O card recompõe essa soma no De/Até — não é o HH Real das Histórias da tabela.',
  },
];

const PERIOD_REPORT_SCOPE_VALUES: readonly PmoReportScopeFilter[] = [
  'carteira',
  'planilha_sap_nsap',
  'planilha_export_worklogs',
];

export const normalizePeriodReportScope = (value: string | null | undefined): PmoReportScopeFilter => (
  PERIOD_REPORT_SCOPE_VALUES.includes(value as PmoReportScopeFilter)
    ? (value as PmoReportScopeFilter)
    : 'carteira'
);

/** Valor enviado ao POST /costs/calculate — null = escopo carteira (sem filtro extra). */
export const toPeriodReportScopeApi = (scope: PmoReportScopeFilter): PeriodReportScopeApi => {
  if (scope === 'planilha_sap_nsap') return 'planilha_sap_nsap';
  if (scope === 'planilha_export_worklogs') return 'planilha_export_worklogs';
  return null;
};

export const periodReportScopeLabel = (scope: PmoReportScopeFilter): string => (
  PERIOD_WORKLOG_SCOPE_OPTIONS.find((item) => item.id === scope)?.label
  || PERIOD_WORKLOG_SCOPE_OPTIONS[0].label
);

export const isExportWorklogPeriodScope = (scope: PmoReportScopeFilter): boolean =>
  scope === 'planilha_export_worklogs';

/**
 * Com a metodologia export, Fase 1/3a (expansão de subtarefas da tabela) não deve
 * rodar — satura o Jira por muitos minutos e infla o KPI de período.
 */
export const shouldHoldExecutiveCostUntilExportSettles = (params: {
  reportScopeApi: PeriodReportScopeApi;
  requiresWorklogPeriodCost: boolean;
  periodCostCalculationPending: boolean;
  hasPeriodCostAnalysis: boolean;
  periodCostError: string;
}): boolean => (
  params.reportScopeApi === 'planilha_export_worklogs' && params.requiresWorklogPeriodCost
);

/**
 * Recorte de linhas do comparativo Compras-e: mantém só as Histórias que tiveram
 * apontamento no De/Até (as mesmas contadas em "Pais com apontamento" na tela Compras-e).
 * Com `parentKeys` nulo o payload ainda não chegou e as linhas passam sem alteração.
 */
export const filterRowsWithWorklogInPeriod = <T extends { key?: string }>(
  rows: T[],
  parentKeys: string[] | null | undefined,
): T[] => {
  if (!Array.isArray(parentKeys)) return rows;
  const allowed = new Set(
    parentKeys.map((item) => String(item || '').trim().toUpperCase()).filter(Boolean),
  );
  return rows.filter((row) => allowed.has(String(row.key || '').trim().toUpperCase()));
};

/** Recorte De/Até do preset comparativo (mesmo intervalo usado no relatório SAP+NSAP). */
export const COMPRAS_E_REPORT_PERIOD_HINT = 'Para o recorte do relatório use De 01/2025 e Até o mês corrente (DINAMICA TOTAL 14.812,77 no XLS de 20/08/2026; o Jira ao vivo pode diferir).';
